import React, { useEffect, useState, useMemo } from 'react';
import {
  Box, Heading, Text, Flex, HStack, Select, Button,
  Table, Thead, Tbody, Tr, Th, Td, TableContainer,
} from '@chakra-ui/react';
import { useParams, useNavigate } from 'react-router-dom';
import LoadingState from '../components/ui/LoadingState';
import EmptyState from '../components/ui/EmptyState';
import Page from '../components/ui/Page';
import SectionCard from '../components/ui/SectionCard';
import Breadcrumbs from '../components/ui/Breadcrumbs';
import StatusBadge from '../components/ui/StatusBadge';
import TestCaseDrawer from '../components/TestCaseDrawer';
import client from '../utils/fetchClient';
import { useProjectContext } from '../context/ProjectContext';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const DOT = { passed: 'status.pass', failed: 'status.fail', skipped: 'status.skip', flaky: 'status.flaky' };

const fmtDuration = (ms) => {
  if (!ms || Number.isNaN(Number(ms))) return '—';
  return Number(ms) >= 1000 ? `${(Number(ms) / 1000).toFixed(1)}s` : `${Math.round(Number(ms))}ms`;
};

// Group cases by their suite/file bucket; fall back to the legacy "Group›Test" title.
function moduleOf(tc) {
  if (tc.suite) return tc.suite;
  if (tc.file) {
    const parts = tc.file.split('/');
    return parts.length >= 2 ? parts[1] : parts[0];
  }
  if (tc.title && tc.title.includes('›')) return tc.title.split('›')[0].replace(/^@/, '').trim();
  return 'Ungrouped';
}

function displayTitle(tc) {
  return tc.title ? tc.title.split('›').pop().trim() : '(untitled)';
}

export default function TestCasesPage() {
  const { group } = useParams();
  const navigate = useNavigate();
  const { projects, selectedProject, setSelectedProject, effectiveProjectParam } = useProjectContext();

  const [testCases, setTestCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCase, setSelectedCase] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    if (!group) {
      navigate('/test-case-groups');
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    const query = { group, pageSize: '200', page: '1' };
    if (effectiveProjectParam) query.project = effectiveProjectParam;
    client.get(`${API_BASE_URL}/api/test-cases`, { query })
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data : (data?.data || data?.items || data?.results || []);
        setTestCases(list);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setTestCases([]);
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [group, effectiveProjectParam, navigate]);

  const groupedByModule = useMemo(() => {
    const acc = {};
    (Array.isArray(testCases) ? testCases : []).forEach((tc) => {
      const mod = moduleOf(tc);
      (acc[mod] = acc[mod] || []).push(tc);
    });
    return acc;
  }, [testCases]);

  const openCase = (tc) => {
    setSelectedCase({ ...tc, testRunId: tc.testRunId });
    setDrawerOpen(true);
  };

  if (!group) return null;
  if (loading) return <LoadingState message={`Loading ${group} test cases`} minH="70vh" />;

  return (
    <Page maxW="7xl">
      <Breadcrumbs
        items={[
          { label: 'Metrics', to: '/metrics' },
          { label: 'Test groups', to: '/test-case-groups' },
          { label: group },
        ]}
      />
      <Flex align="flex-start" justify="space-between" wrap="wrap" gap={3} mb={5}>
        <Box>
          <Heading size="lg" fontWeight="800" letterSpacing="-0.5px" color="gray.900">{group}</Heading>
          <Text fontSize="sm" color="gray.500">
            {testCases.length} case{testCases.length === 1 ? '' : 's'} · click a row for history, error and AI analysis
          </Text>
        </Box>
        <HStack spacing={2}>
          <Button size="sm" variant="outline" borderRadius="full" onClick={() => navigate(`/test-groups/${encodeURIComponent(group)}/history`)}>
            Run history
          </Button>
          <Select
            display={{ base: 'block', md: 'none' }}
            value={selectedProject || 'ALL PROJECTS'}
            onChange={(e) => setSelectedProject(e.target.value)}
            size="sm"
            borderRadius="full"
            maxW="180px"
            bg="white"
          >
            <option value="ALL PROJECTS">All Projects</option>
            {projects.map((p, idx) => (
              <option key={(p.id ?? p) + '-' + idx} value={p.id ?? p}>{p.name ?? p}</option>
            ))}
          </Select>
        </HStack>
      </Flex>

      {testCases.length === 0 ? (
        <EmptyState emoji="🧪" title="No test cases found" subtitle="This group has no cases for the current project or period." />
      ) : (
        Object.entries(groupedByModule).map(([mod, cases]) => (
          <SectionCard key={mod} mb={5}>
            <Heading as="h3" size="sm" color="gray.900" mb={3}>{mod}</Heading>

            <TableContainer display={{ base: 'none', md: 'block' }}>
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr>
                    <Th>Test</Th>
                    <Th>Status</Th>
                    <Th>Location</Th>
                    <Th isNumeric>Duration</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {cases.map((tc) => (
                    <Tr key={tc.id} _hover={{ bg: 'gray.50', cursor: 'pointer' }} onClick={() => openCase(tc)}>
                      <Td>
                        <HStack spacing={2.5}>
                          <Box w="8px" h="8px" borderRadius="full" bg={DOT[tc.status] || 'gray.300'} flexShrink={0} />
                          <Text color="gray.900">{displayTitle(tc)}</Text>
                        </HStack>
                      </Td>
                      <Td><StatusBadge status={tc.status} /></Td>
                      <Td fontFamily="mono" fontSize="11px" color="gray.500">
                        {tc.file || tc.suite || '—'}{tc.line ? `:${tc.line}` : ''}
                      </Td>
                      <Td isNumeric fontFamily="mono" fontSize="12px">{fmtDuration(tc.duration)}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>

            <Box display={{ base: 'block', md: 'none' }}>
              {cases.map((tc) => (
                <Flex
                  key={tc.id}
                  onClick={() => openCase(tc)}
                  align="center"
                  gap={3}
                  py={3}
                  borderBottomWidth="1px"
                  borderColor="gray.100"
                  cursor="pointer"
                >
                  <Box w="8px" h="8px" borderRadius="full" bg={DOT[tc.status] || 'gray.300'} flexShrink={0} />
                  <Box flex="1" minW={0}>
                    <Text fontWeight="600" color="gray.900" noOfLines={1}>{displayTitle(tc)}</Text>
                    <Text fontSize="11px" color="gray.400" fontFamily="mono" noOfLines={1}>{tc.file || tc.suite || '—'}</Text>
                  </Box>
                  <StatusBadge status={tc.status} />
                </Flex>
              ))}
            </Box>
          </SectionCard>
        ))
      )}

      <TestCaseDrawer
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); setSelectedCase(null); }}
        testCase={selectedCase}
      />
    </Page>
  );
}
